/**
 * Helpers for the Apollo error link's network-error branch.
 *
 * Two transport paths reach that branch: the HTTP link (queries/mutations) and
 * the Amplify AppSync subscription link. The subscription link forwards
 * Amplify's plain-object errors unchanged, and Apollo — which only understands
 * Error instances — wraps them in `UnconventionalError`, whose message is the
 * opaque "An error of unexpected shape occurred." Unwrapping that wrapper lets
 * the link classify and report the real cause instead of the wrapper.
 */
import { UnconventionalError } from "@apollo/client/errors"

/**
 * Returns the underlying cause of an `UnconventionalError` (the original
 * non-Error value Apollo wrapped), or the error itself when it is not wrapped.
 * Use this before classifying or describing a network error so subscription
 * errors are seen as their real cause rather than the wrapper.
 */
export const unwrapUnconventionalError = (error: unknown): unknown =>
  UnconventionalError.is(error) ? error.cause : error

const safeStringify = (value: unknown, fallback: string): string => {
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

/**
 * Extracts a useful RUM message and name from a (possibly non-Error) network
 * error. Amplify subscription errors arrive as plain objects that commonly
 * carry an `errors` array whose entries each have a `message`; this surfaces
 * those messages instead of the generic wrapper text so RUM shows what
 * actually broke.
 *
 * @param error - the unwrapped network error (see {@link unwrapUnconventionalError})
 * @param fallbackMessage - message to use when nothing better can be derived
 */
export const describeNetworkError = (
  error: unknown,
  fallbackMessage: string
): { message: string; name: string } => {
  if (error instanceof Error) {
    return { message: error.message, name: error.name || "NetworkError" }
  }

  if (error && typeof error === "object") {
    const { name, message, errors } = error as {
      name?: string
      message?: string
      errors?: Array<{ message?: string }>
    }
    const derived =
      Array.isArray(errors) && errors.length > 0
        ? errors.map((entry) => entry.message ?? "unknown").join("; ")
        : message ?? safeStringify(error, fallbackMessage)
    return { message: derived, name: name ?? "SubscriptionError" }
  }

  return { message: fallbackMessage, name: "NetworkError" }
}
